import { useQuery } from '@tanstack/react-query';
import type { ApiClient } from '../../api/client';
import type { Product } from '../../types';
import { formatPrice, productTypeLabel } from '../../lib/format';
import { Spinner } from '../Spinner';
import styles from '../BookingWidget.module.css';

interface ProductStepProps {
  api: ApiClient;
  selectedId: string | null;
  onSelect: (product: Product) => void;
}

export function ProductStep({ api, selectedId, onSelect }: ProductStepProps) {
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['products'],
    queryFn: () => api.getProducts(),
  });

  if (isLoading) {
    return <Spinner centered large />;
  }

  if (isError) {
    return (
      <div>
        <p className={styles.error}>
          {error instanceof Error
            ? error.message
            : 'Angebote konnten nicht geladen werden.'}
        </p>
        <button type="button" className={styles.card} onClick={() => refetch()}>
          Erneut versuchen
        </button>
      </div>
    );
  }

  if (!data?.length) {
    return (
      <p className={styles.empty}>
        Keine buchbaren Angebote gefunden. Bitte kontaktiere das Studio.
      </p>
    );
  }

  return (
    <div className={styles.list}>
      {data.map((product) => {
        const isSelected = product.id === selectedId;
        const isFree = product.priceCents === 0;

        return (
          <button
            key={product.id}
            type="button"
            className={isSelected ? styles.cardSelected : styles.card}
            onClick={() => onSelect(product)}
          >
            <h3 className={styles.cardTitle}>{product.name}</h3>
            <p className={styles.cardMeta}>{productTypeLabel(product.type)}</p>
            {product.description ? (
              <p className={styles.cardMeta}>{product.description}</p>
            ) : null}
            {product.creditCount ? (
              <p className={styles.cardMeta}>{product.creditCount} Credits</p>
            ) : null}
            <p className={styles.cardPrice}>
              {isFree ? 'Kostenlos' : formatPrice(product.priceCents, product.currency)}
            </p>
          </button>
        );
      })}
    </div>
  );
}
